import { useState, useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { useBrands } from "@/lib/BrandsProvider";
import { supabase } from "@/lib/supabase";
import { Button } from "@/components/ui/button";
import { LoadingSpinner } from "@/components/ui/loading-spinner";
import { Card } from "@/components/ui/card";
import { Alert, AlertTitle, AlertDescription } from "@/components/ui/alert";
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { AlertCircle, Download, FileText, Palette } from "lucide-react";
import BrandThemeWrapper from "./BrandThemeWrapper";
import BrandTheme from './BrandTheme';
import { BrandProgress } from './BrandProgress';
import html2canvas from "html2canvas";
import jsPDF from "jspdf";

// Define a type for the brand data with brand_kit_json field
interface BrandWithKit {
  id: string;
  brand_name: string;
  brand_kit_json?: any;
}

export function BrandGuide() {
  const { selectedBrand } = useBrands();
  const [searchParams] = useSearchParams();
  const [brandKit, setBrandKit] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasAutoGenerated, setHasAutoGenerated] = useState(false);
  const brandKitHtmlRef = useRef<HTMLDivElement>(null);
  const brandKitJsonRef = useRef<HTMLPreElement>(null);

  useEffect(() => {
    async function fetchBrandKit() {
      if (!selectedBrand) return;

      try {
        setIsLoading(true);
        
        // Execute a query to get the brand data
        const { data, error } = await supabase
          .from('brands')
          .select('*')
          .eq('id', selectedBrand.id)
          .single();

        if (error) throw error;
        
        // Type assertion to handle the brand_kit_json field
        const brandData = data as unknown as BrandWithKit;
        setBrandKit(brandData?.brand_kit_json || null);
      } catch (err) {
        console.error('Error fetching brand kit:', err);
        setError('Failed to load brand kit. Please try again later.');
      } finally {
        setIsLoading(false);
      }
    }

    fetchBrandKit();
  }, [selectedBrand]);

  // Check for autoGenerate parameter and trigger generation
  useEffect(() => {
    const shouldAutoGenerate = searchParams.get('autoGenerate') === 'true';
    
    if (shouldAutoGenerate && !isLoading && !brandKit && !hasAutoGenerated && selectedBrand) {
      console.log('🔍 DEBUG: Auto-generating brand kit from URL parameter');
      setHasAutoGenerated(true);
      generateBrandKit();
    }
  }, [searchParams, isLoading, brandKit, hasAutoGenerated, selectedBrand]);

  const generateBrandKit = async () => {
    if (!selectedBrand) return;

    try {
      setIsGenerating(true);
      setError(null);

      const workerUrl = import.meta.env.VITE_WORKERS_URL;
      const endpoint = `${workerUrl}/rainfactory/brand-genius/generate_brand_kit`;
      console.log('🔍 DEBUG: Calling endpoint:', endpoint);
      
      let result;

      try {
          const session = await supabase.auth.getSession();
          console.log('🔍 DEBUG: Auth session available:', !!session.data.session);
          
          const response = await fetch(endpoint, {
              method: 'POST',
              headers: {
                  'Content-Type': 'application/json',
                  'Authorization': session.data.session ? `Bearer ${session.data.session.access_token}` : '',
              },
              body: JSON.stringify({
                  brandId: selectedBrand.id
              })
          });
          
          result = await response.json();
          console.log('🔍 DEBUG: Received Brand Kit', result);
          
          if (!response.ok) {
            throw new Error(`Failed to generate brand kit: ${response.statusText}`);
          }
      } catch (fetchError) {
          throw new Error(`Failed to generate brand kit: ${fetchError}`);
      }

      // Update local state
      setBrandKit(result.brand_kit_json);
      
      // Refresh the brand data
      await fetchBrandKit();
    } catch (err) {
      console.error('Error generating brand kit:', err);
      setError('Failed to generate brand kit. Please try again later.');
    } finally {
      setIsGenerating(false);
    }
  };

  // Function to fetch brand kit data
  async function fetchBrandKit() {
    if (!selectedBrand) return;

    try {
      const { data, error } = await supabase
        .from('brands')
        .select('*')
        .eq('id', selectedBrand.id)
        .single();

      if (error) throw error;
      
      // Type assertion to handle the brand_kit_json field
      const brandData = data as unknown as BrandWithKit;
      setBrandKit(brandData?.brand_kit_json || null);
    } catch (err) {
      console.error('Error refreshing brand kit:', err);
    }
  }

  // Function to export the brand kit as PDF
  const exportToPDF = async () => {
    if (!selectedBrand) return;
    
    try {
      setIsExporting(true);
      setError(null);

      // Determine which element to capture based on what's rendered
      const element = brandKit?.html ? brandKitHtmlRef.current : brandKitJsonRef.current;
      
      if (!element) {
        throw new Error("Content not found for export");
      }

      const canvas = await html2canvas(element, {
        scale: 2, // Higher scale for better quality
        useCORS: true, // To handle images from different origins
        logging: false
      });
      
      const imgData = canvas.toDataURL('image/png');
      
      // A4 dimensions in mm: 210 x 297
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4'
      });
      
      const imgWidth = 210;
      const imgHeight = canvas.height * imgWidth / canvas.width;
      
      // If the content is longer than one page, create multiple pages
      let heightLeft = imgHeight;
      let position = 0;
      let pageHeight = 297; // A4 page height
      
      pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;
      
      while (heightLeft >= 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }
      
      pdf.save(`${selectedBrand.brand_name} Brand Kit.pdf`);
    } catch (err) {
      console.error('Error exporting PDF:', err);
      setError('Failed to export PDF. Please try again later.');
    } finally {
      setIsExporting(false);
    }
  };

  if (!selectedBrand) {
    return (
      <div className="flex items-center justify-center h-96">
        <Alert variant="destructive" className="max-w-md">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>No brand selected</AlertTitle>
          <AlertDescription>
            Please select a brand to view its brand kit.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="container mx-auto py-8 max-w-6xl">
      <div className="mb-8 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">{selectedBrand.brand_name} Brand Kit</h1>
          <p className="text-muted-foreground mt-1">
            Manage your brand kit and visual theme in one place.
          </p>
        </div>
      </div>

      {error && (
        <Alert variant="destructive" className="mb-6">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Error</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <Tabs defaultValue="brand-kit" className="space-y-6">
        <TabsList className="grid w-full grid-cols-2 md:w-auto md:inline-flex">
          <TabsTrigger value="brand-kit">
            <FileText className="mr-2 h-4 w-4" />
            Brand Kit
          </TabsTrigger>
          <TabsTrigger value="theme">
            <Palette className="mr-2 h-4 w-4" />
            Theme Editor
          </TabsTrigger>
        </TabsList>

        {/* Brand Kit Tab */}
        <TabsContent value="brand-kit" className="space-y-6">
          <BrandProgress brand={selectedBrand} />
          
          {/* Actions Bar */}
          {brandKit && (
            <div className="flex flex-col sm:flex-row gap-3 justify-between items-start sm:items-center">
              <div className="flex flex-col sm:flex-row gap-3">
                <Button 
                  onClick={generateBrandKit} 
                  disabled={isGenerating || isExporting}
                  variant="outline"
                  size="sm"
                >
                  {isGenerating && <LoadingSpinner className="mr-2" size="sm" />}
                  Refresh Brand Kit
                </Button>
              </div>
              
              <Button
                onClick={exportToPDF}
                disabled={isGenerating || isExporting}
                size="sm"
              >
                {isExporting ? (
                  <LoadingSpinner className="mr-2" size="sm" />
                ) : (
                  <Download className="mr-2 h-4 w-4" />
                )}
                Export PDF
              </Button>
            </div>
          )}

          {/* Content Area */}
          {isGenerating ? (
            <Card className="p-8">
              <div className="flex flex-col items-center justify-center text-center space-y-4">
                <LoadingSpinner size="lg" />
                <div>
                  <h3 className="text-lg font-semibold mb-2">Generating Your Brand Kit</h3>
                  <p className="text-muted-foreground">
                    We're analyzing your brand and creating a comprehensive guide. This may take a moment...
                  </p>
                </div>
              </div>
            </Card>
          ) : brandKit ? (
            brandKit.html ? (
              <Card className="p-6">
                <BrandThemeWrapper brandId={selectedBrand.id} contentType="landing-page">
                  <div
                    ref={brandKitHtmlRef}
                    className="prose max-w-none"
                    dangerouslySetInnerHTML={{ __html: brandKit.html }}
                  />
                </BrandThemeWrapper>
              </Card>
            ) : (
              <Card className="p-6">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-semibold">Brand Kit Data</h3>
                  <p className="text-sm text-muted-foreground">JSON format</p>
                </div>
                <pre 
                  ref={brandKitJsonRef}
                  className="whitespace-pre-wrap overflow-auto max-h-[80vh] text-sm bg-muted/30 p-4 rounded-md"
                >
                  {JSON.stringify(brandKit, null, 2)}
                </pre>
              </Card>
            )
          ) : (
            <Card className="p-8">
              <div className="flex flex-col items-center justify-center text-center space-y-6">
                <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center">
                  <FileText className="w-8 h-8 text-primary" />
                </div>
                <div className="space-y-2">
                  <h3 className="text-xl font-semibold">Create Your Brand Kit</h3>
                  <p className="text-muted-foreground max-w-md">
                    Generate a comprehensive brand kit that includes your brand's identity, 
                    voice, visual guidelines, and messaging strategy.
                  </p>
                </div>
                <Button 
                  onClick={generateBrandKit} 
                  disabled={isGenerating}
                  size="lg"
                  className="mt-4"
                >
                  {isGenerating && <LoadingSpinner className="mr-2" size="sm" />}
                  Generate Brand Kit
                </Button>
              </div>
            </Card>
          )}
        </TabsContent>

        {/* Theme Editor Tab */}
        <TabsContent value="theme" className="space-y-4">
          <BrandTheme key={selectedBrand.id} />
        </TabsContent>
      </Tabs>
    </div>
  );
} 